import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

/**
 * Home banner announcing the current driver-app build.
 *
 * Reads the release marked current in Maintain Mobile App and gives supervisors the share link
 * right where they start their day, so getting a new build onto drivers' phones is copy-and-send
 * rather than a hunt through a menu.
 *
 * Dismissal is remembered per version, so the banner comes back on its own for the next release.
 */

const DISMISS_KEY = "shypv_app_update_dismissed_version";

export default function AppUpdateBanner({ platform = "ANDROID" }) {
  const API_URL = process.env.REACT_APP_API_URL;

  const [latest, setLatest] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}shypvdriverapp/mobile-app/latest`, { params: { platform } });
      const data = (res.data && res.data.data) || null;
      if (!data || !data.versionName) return;

      let seen = null;
      try {
        seen = window.localStorage.getItem(DISMISS_KEY);
      } catch (e) {
        // private browsing / storage disabled — just always show it
      }
      setLatest(data);
      setDismissed(seen === `${platform}:${data.versionName}`);
    } catch (err) {
      // The banner is an extra; a dashboard must not break because a release lookup failed.
    }
  }, [API_URL, platform]);

  useEffect(() => {
    load();
  }, [load]);

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, `${platform}:${latest.versionName}`);
    } catch (e) {
      /* nothing to remember it with — it will show again next load */
    }
  };

  const copyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(latest.shareUrl);
      } else {
        const ta = document.createElement("textarea");
        ta.value = latest.shareUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      window.prompt("Copy this link:", latest.shareUrl);
    }
  };

  const share = () => {
    const text =
      `Shypv Driver App ${latest.versionName} is available.\n` +
      `Download and install: ${latest.shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  if (!latest || dismissed) return null;

  const btn = {
    border: "1px solid rgba(255,255,255,.45)",
    background: "rgba(255,255,255,.14)",
    color: "#fff",
    borderRadius: 8,
    padding: "7px 12px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
    whiteSpace: "nowrap",
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
        background: "linear-gradient(90deg,#0b7fc4,#00a6ed)",
        color: "#fff",
        borderRadius: 12,
        padding: "14px 18px",
        margin: "0 0 18px",
        boxShadow: "0 4px 16px rgba(0,124,190,.22)",
      }}
    >
      <i className="fa fa-mobile" aria-hidden="true" style={{ fontSize: 26, opacity: 0.9 }} />

      <div style={{ flex: "1 1 260px", minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>
          Driver app {latest.versionName} is available
          {latest.forceUpdate ? " — mandatory update" : ""}
        </div>
        <div style={{ fontSize: 13, opacity: 0.92, marginTop: 2 }}>
          {latest.fileSizeLabel ? `${latest.fileSizeLabel} · ` : ""}
          Share the download link with your drivers.
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flex: "0 0 auto" }}>
        <button type="button" style={btn} onClick={copyLink}>
          <i className={`fa ${copied ? "fa-check" : "fa-link"}`} aria-hidden="true" /> {copied ? "Copied" : "Copy link"}
        </button>
        <button type="button" style={btn} onClick={share}>
          <i className="fa fa-whatsapp" aria-hidden="true" /> Share
        </button>
        <a style={btn} href={latest.shareUrl} target="_blank" rel="noreferrer">
          <i className="fa fa-download" aria-hidden="true" /> Download
        </a>
        <button
          type="button"
          onClick={dismiss}
          title="Hide until the next release"
          style={{ ...btn, border: "none", background: "transparent", padding: "7px 4px" }}
        >
          <i className="fa fa-times" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
